import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import {
	useProfileInfoUserContextQuery,
	type useProfileInfoUserContextQuery$data,
	type useProfileInfoUserContextQuery$variables,
} from '@confluence/profile-macros/entry-points/useProfileInfoUserContextQuery.ts';

import type {
	MacroContentNode,
	MacroPreloadContext,
	MacroPreloadHandler,
} from '../preloadMacroData.ts';

/**
 * Preload handler for the user-profile macro (extensionKey `profile`).
 *
 * For a profile extension node found in the ADF content, this handler
 * builds query variables from the macro's `User` parameter and fires the
 * useProfileInfoUserContextQuery GraphQL query to populate the Relay store
 * so the renderer's useQueryInterop call returns synchronously on the client.
 */
export const userProfilePreloadHandler: MacroPreloadHandler = {
	extensionKey: 'profile',

	routes: ['view', 'space-overview', 'edit', 'live'],

	preload: async (node: MacroContentNode, _context: MacroPreloadContext): Promise<void> => {
		try {
			const userId = node.attrs?.parameters?.macroParams?.user?.value;
			if (!userId) {
				return;
			}
			const variables: useProfileInfoUserContextQuery$variables = { userId };
			await query<useProfileInfoUserContextQuery$data, useProfileInfoUserContextQuery$variables>({
				query: useProfileInfoUserContextQuery,
				variables,
				context: { single: true },
			});
		} catch {
			// Preloading is best-effort cache warming. Ignore failures here and
			// let the renderer perform the query in its normal error-handled path.
			return;
		}
	},
};
